export const stageKeys = ['category', 'sector', 'impactTriage', 'dimension', 'geography', 'gaps']

export const classificationTone = {
  essential: 'error',
  important: 'warning',
  future_important: 'warning',
  public: 'info',
  foreign: 'secondary',
  indirect: 'default',
  out_of_scope: 'success',
}

const readinessMeasureMeta = {
  governance: { key: 'governance' },
  incidents: { key: 'incidents' },
  continuity: { key: 'continuity' },
  supplyChain: { key: 'supplyChain' },
  mfa: { key: 'mfa' },
}

const impactTriageQuestionIds = new Set([
  'triageSoleProvider',
  'triageSevereImpact',
  'triageStateNotifiedCriticalEntity',
])

function translateHelperText(helperText, t) {
  if (!helperText) {
    return helperText
  }

  const translated = t(`questionHelpers.${helperText}`, { defaultValue: '' })
  if (translated) {
    return translated
  }

  if (helperText.includes('Essencial')) {
    return t('helpers.essential')
  }

  if (helperText.includes('Importante')) {
    return t('helpers.important')
  }

  return helperText
}

function translateNotes(question, t) {
  if (!Array.isArray(question.notes)) {
    return question.notes
  }

  return question.notes.map((note) => t(`questionNotes.${note}`, { defaultValue: note }))
}

function translateOptionLabel(question, option, t) {
  if (question.id === 'sectorCollection') {
    return t(`options.sectors.${option.value}`, { defaultValue: option.label })
  }

  if (question.id === 'primarySector') {
    return t(`options.sectors.${option.value}`, { defaultValue: option.label })
  }

  if (question.id === 'subsector') {
    return t(`options.subsectors.${option.value}`, { defaultValue: option.label })
  }

  if (question.id === 'digitalInfrastructureTypes') {
    return t(`options.digitalInfrastructureTypes.${option.value}`, { defaultValue: option.label })
  }

  if (question.id.startsWith('gap_')) {
    return t(`options.gapStatus.${option.value}`, { defaultValue: option.label })
  }

  if (question.type === 'boolean') {
    return t(`options.boolean.${String(option.value)}`, { defaultValue: option.label })
  }

  if (question.id === 'publicEntityCategory') {
    return t(`options.publicEntityCategory.${option.value}`, { defaultValue: option.label })
  }

  if (question.id === 'organizationCategory') {
    return t(`options.organizationCategory.${option.value}`, { defaultValue: option.label })
  }

  return t(`options.${question.id}.${option.value}`, { defaultValue: option.label })
}

function translateOptionDescription(question, option, t) {
  if (impactTriageQuestionIds.has(question.id)) {
    const noteKey = `${question.id}_${option.value ? 'select' : 'skip'}`
    return t(`questionNotes.${noteKey}`, { defaultValue: option.description })
  }

  if (question.id === 'organizationCategory') {
    return t(`optionDescriptions.organizationCategory.${option.value}`, { defaultValue: option.description })
  }

  if (question.id === 'digitalInfrastructureTypes') {
    return t(`optionDescriptions.digitalInfrastructureTypes.${option.value}`, { defaultValue: option.description })
  }

  return option.description
}

export function translateQuestion(question, t) {
  if (!question) {
    return question
  }

  const value =
    question.id === 'sectorCollection' && question.value
      ? {
          ...question.value,
          availablePrimarySectors: (question.value.availablePrimarySectors || []).map((option) => ({
            ...option,
            label: t(`options.sectors.${option.value}`, { defaultValue: option.label }),
          })),
          subsectorOptions: Object.fromEntries(
            Object.entries(question.value.subsectorOptions || {}).map(([key, options]) => [
              key,
              options.map((option) => ({
                ...option,
                label: t(`options.subsectors.${option.value}`, { defaultValue: option.label }),
              })),
            ]),
          ),
        }
      : question.value

  return {
    ...question,
    stageLabel: t(`stages.${stageKeys[(question.stage || 1) - 1]}`),
    prompt: t(`questions.${question.id}.prompt`, { defaultValue: question.prompt }),
    helperText: translateHelperText(question.helperText, t),
    notes: translateNotes(question, t),
    value,
    options: question.options.map((option) => ({
      ...option,
      label: translateOptionLabel(question, option, t),
      description: translateOptionDescription(question, option, t),
    })),
  }
}

export function classificationLabel(report, t) {
  return t(`classifications.${report.classification || 'pending'}`, {
    defaultValue: report.classificationLabel,
  })
}

export function classificationReason(report, t) {
  return t(`classificationReasons.${report.classification}`, {
    defaultValue: report.reason || t('report.fallbackReason'),
  })
}

export function sanctionTitle(report, t) {
  const key = report.sanctionKey || (report.classification === 'public' ? report.publicEntityGroup || 'public' : report.classification || 'none')
  return t(`sanctions.titles.${key}`, { defaultValue: report.sanction.title })
}

export function sanctionDetail(report, t) {
  const key = report.sanctionKey || (report.classification === 'public' ? report.publicEntityGroup || 'public' : report.classification || 'none')
  return t(`sanctions.details.${key}`, { defaultValue: report.sanction.detail })
}

export function getReadinessMeasureMeta(id) {
  return readinessMeasureMeta[id] || { key: id }
}
